import { TranslateService } from "@ngx-translate/core";
import { AngularSvgIconModule } from "angular-svg-icon";
import { map, startWith } from "rxjs";

import { ChangeDetectionStrategy, Component, computed, inject, input, output } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { MatTooltipModule } from "@angular/material/tooltip";

import { IotnetGateway } from "../../../apis/stats/rest/models";
import { StatusIcon } from "../../common/status-icon/status-icon.component";
import { ServiceTags } from "../../subscriptions-list/service-tags/service-tags";
import { hasCoordinates } from "../iotnet-gateway-helpers";
import { gatewayTags } from "../iotnet-gateway-tags";

@Component({
  selector: "iotnet-gateway-element",
  templateUrl: "iotnet-gateway-element.html",
  styleUrls: ["./iotnet-gateway-element.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [AngularSvgIconModule, MatTooltipModule, ServiceTags, StatusIcon],
})
export class IotnetGatewayElement {
  private readonly translate = inject(TranslateService);
  private readonly currentLang = toSignal(
    this.translate.onLangChange.pipe(
      map((e) => e.lang),
      startWith(this.translate.getCurrentLang()),
    ),
    { requireSync: true },
  );

  public readonly gateway = input.required<IotnetGateway>();
  public readonly active = input(false);
  public readonly selected = output<IotnetGateway>();
  public readonly deselected = output<IotnetGateway>();
  protected readonly health = computed(() => this.gateway().health);
  protected readonly tags = computed(() => gatewayTags(this.gateway()));

  protected readonly lastSeen = computed<Date | null>(() => {
    const unix = this.gateway().last_seen;
    return unix ? new Date(unix * 1000) : null;
  });
  protected readonly warn = computed(() => !hasCoordinates(this.gateway()) || !this.lastSeen());

  protected readonly warning = computed(() => {
    this.currentLang(); // re-evaluate the labels on language change
    const warnings: string[] = [];
    if (!this.lastSeen()) warnings.push(this.translate.instant("Iotnet.Warning.Unseen") as string);
    if (!hasCoordinates(this.gateway())) warnings.push(this.translate.instant("Iotnet.Warning.Positionless") as string);
    return warnings.join(" ");
  });

  protected select(): void {
    this.selected.emit(this.gateway());
  }

  protected deselect(event: Event): void {
    event.stopPropagation(); // do not re-select via the card's click handler
    this.deselected.emit(this.gateway());
  }
}
